import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, Phone, Plus, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/uiStore'
import { useAssistantStore } from '@/stores/assistantStore'
import { useCallStore } from '@/stores/callStore'
import { MobileDockShell } from '@/components/ui/MobileDockShell'
import { WheelMark } from './WheelMark'
import { usePermissions } from '@/hooks/usePermission'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { navItems, visibleNavItems, type NavItem } from './navItems'
import { useFabClearance } from './useFabClearance'
import {
  allowedCreateActions,
  createMenuShownOn,
  orderCreateActions,
  useCreateMenuSuppressed,
} from './CreateMenu'
import {
  BUBBLE,
  RADIUS,
  GUTTER_X,
  TRIGGER,
  WHEEL_COVER_ABOVE_DOCK,
  angleDelta,
  atRest,
  itemAngle,
  itemOffset,
  nearestIndex,
  pointerAngle,
  releaseTarget,
  rubberBand,
  scrollBy,
  springEasing,
  springStep,
  openPosition,
  visibility,
  type SpringState,
} from './wheelMath'

/**
 * The phone's one navigation control (replaces the bottom dock, the More card
 * and the "+" button). A dark glass bubble carrying the orbit-dots mark (WheelMark, in aurora colours) sits bottom-right, in the thumb zone.
 * Tapping it pops every destination out as bubbles along a quarter circle
 * centred on it: they spring out from under it with a small pop and tuck back
 * under it on close. The motion is plain CSS transforms with a spring curve, so
 * the GPU runs it (the earlier libraries.dev goo filter repainted on the CPU
 * every frame and stuttered on iPhone). Dragging a thumb around the corner
 * turns the wheel; it settles on a bubble with a spring. Tap a bubble to go.
 *
 * "Create" is a bubble on the wheel: it swaps the ring for the create actions.
 *
 * Phones only (md:hidden): from 768px up the sidebar is the navigation.
 */

/** Gap the control keeps above the screen bottom: GUTTER_X (44px) or 10px above the iOS home indicator, so it sits in from the corner on the diagonal. */
export const DOCK_GAP = 'max(44px, calc(var(--mobile-dock-safe-bottom, 0px) + 10px))'

/** The layer is a square whose bottom-right corner is the trigger's bottom-right corner. */
const BOX = RADIUS + TRIGGER / 2 + BUBBLE / 2 + 20
const CENTRE = BOX - TRIGGER / 2 // trigger centre, in box coordinates (x and y)
const DRAG_START_PX = 8
// The trigger and dock: dark glass with an edge bright enough to find in sunlight (bubbles: .wheel-orb).
const FILL = 'color-mix(in srgb, rgb(var(--fg-rgb)) 10%, rgb(var(--bg-rgb)))'
const EDGE = '1.5px solid rgb(var(--fg-rgb) / 0.3)'
const SHADOW = '0 10px 24px -8px rgba(0,0,0,0.7)'
// Open: a stiff, slightly underdamped spring (a small bubbly pop), baked into a CSS linear() curve
// so the compositor runs it on the GPU, not JavaScript frame by frame. Close: a short ease-in back
// under the trigger. Browsers without linear() get a cubic-bezier with a similar overshoot.
const OPEN = springEasing(700, 34)
const OPEN_EASE = typeof CSS !== 'undefined' && CSS.supports?.('transition-timing-function', 'linear(0, 1)')
  ? OPEN.easing
  : 'cubic-bezier(0.34, 1.45, 0.64, 1)'
const CLOSE = { duration: 170, easing: 'cubic-bezier(0.4, 0, 1, 1)' }
const STAGGER_MS = 12
const FOLD_MS = 260

type IconType = React.ComponentType<{ className?: string }>
interface WheelItem {
  id: string
  label: string
  icon: IconType
  tone: NavItem['tone']
  active: boolean
  run: () => void
}

/** How much bigger the bubble at the focus angle gets (1.2×; neighbours sit 67px apart, bubbles are 58px). */
const FOCUS_GROW = 0.2

/** Where each destination sits on the ring (owner order, docs/ux/DECISIONS.md §21), then the rest. */
const ROUTE_ORDER = ['/work', '/calendar', '/dashboard', '/inbox', 'phone', 'assistant', '/customers', '/workflows', '/marketing', '/reports', '/settings']

/** "Needs you": the rose accent with a black ring so it reads on the white bubble. */
function UnreadDot({ testId, className }: { testId: string; className: string }) {
  return (
    <span
      data-testid={testId}
      aria-hidden="true"
      className={cn('absolute h-3 w-3 rounded-full', className)}
      style={{ background: 'var(--midnight-rose, var(--rose))', boxShadow: '0 0 0 2px rgb(var(--bg-rgb))' }}
    />
  )
}

const isOn = (pathname: string, path: string) => pathname === path || pathname.startsWith(path + '/')

export function WheelNav() {
  const { pathname, search } = useLocation()
  const { can } = usePermissions()
  // Zero-clip: this stacked list places filters and Edit reason across the
  // full phone width. Unmount the wheel (including an open portal) on Lost;
  // the in-page Open switch restores phone navigation.
  if (can('opportunities.view') && pathname.replace(/\/$/, '') === '/work' && new URLSearchParams(search).get('outcome') === 'lost') return null
  return <WheelNavControl />
}

function WheelNavControl() {
  const { can } = usePermissions()
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const reduced = useReducedMotion()
  const searchOpen = useUIStore((s) => s.searchOpen)
  const inboxBadge = useUIStore((s) => s.inboxBadge)
  const phoneOpen = useCallStore((s) => s.softphoneExpanded)
  const setPhoneOpen = useCallStore((s) => s.setSoftphoneExpanded)
  const assistantOpen = useAssistantStore((s) => s.open)
  const setAssistantOpen = useAssistantStore((s) => s.setOpen)
  // A ringing call or the open call screen owns the whole phone: the wheel
  // stepped aside so it never sits on Accept or the call notes.
  const callOwnsScreen = useCallStore(
    (s) => (s.callStatus === 'ringing' && s.callDirection === 'inbound') || (s.softphoneExpanded && !!s.activeCall),
  )
  const createSuppressed = useCreateMenuSuppressed()
  const menuId = useId()

  const anchorRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const fabRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const stepAside = useFabClearance(fabRef)

  const [mode, setMode] = useState<'nav' | 'create'>('nav')
  // `shown` = layer mounted; `open` = it is the active menu; `out` = bubbles extended.
  // They go up in that order, a frame apart, so the bubbles start at the trigger and spring out.
  const [shown, setShown] = useState(false)
  const [layerVisible, setLayerVisible] = useState(false) // visible while open and while folding back
  const [open, setOpen] = useState(false)

  // While the arc is up (including the fold back under the trigger), the list
  // scrollport grows this band so bubbles sit on the canvas, not on a row.
  useLayoutEffect(() => {
    const root = document.documentElement
    const list = document.querySelector<HTMLElement>('.main-scroll-area.fab-list-clear')
    const atEnd = list && list.scrollHeight - list.clientHeight - list.scrollTop <= 24
    root.style.setProperty('--wheel-cover', layerVisible ? `${WHEEL_COVER_ABOVE_DOCK}px` : '0px')
    // Keep the last row in view when opening or closing changes the scrollport.
    if (atEnd && list) list.scrollTop = list.scrollHeight
  }, [layerVisible])
  useLayoutEffect(() => () => {
    document.documentElement.style.setProperty('--wheel-cover', '0px')
  }, [])
  const [out, setOut] = useState(false)
  const [anchor, setAnchor] = useState<{ bottom: number; right: number } | null>(null)
  // After the first touch the wheel is driven by the finger or the spring, frame by frame:
  // the CSS entrance transition must not fight it.
  const [live, setLive] = useState(false)
  const [p, setP] = useState(0) // drawn scroll position, in items
  const rawRef = useRef(0) // unresisted scroll position
  const springRef = useRef<SpringState>({ p: 0, v: 0 })
  const rafRef = useRef(0)
  const dragRef = useRef<{ id: number; x0: number; y0: number; last: number; moved: boolean; v: number; t: number } | null>(null)
  const suppressClick = useRef(false)

  // ── The ring's contents ──────────────────────────────────────────────
  const routeItems = useMemo(() => {
    const visible = visibleNavItems(navItems, can)
    const byPath = new Map(visible.map((i) => [i.path, i]))
    const ordered: WheelItem[] = []
    const route = (path: string) => {
      const i = byPath.get(path)
      if (!i) return
      byPath.delete(path)
      ordered.push({ id: path, label: i.label, icon: i.icon, tone: i.tone, active: isOn(pathname, path), run: () => navigate(path) })
    }
    for (const key of ROUTE_ORDER) {
      if (key === 'phone') {
        ordered.push({ id: 'phone', label: 'Phone', icon: Phone, tone: 'mint', active: phoneOpen, run: () => setPhoneOpen(!phoneOpen) })
      } else if (key === 'assistant') {
        ordered.push({ id: 'assistant', label: 'Ask AI', icon: Sparkles, tone: 'lavender', active: assistantOpen, run: () => setAssistantOpen(!assistantOpen) })
      } else route(key)
    }
    for (const path of [...byPath.keys()]) route(path)
    return ordered
  }, [can, pathname, navigate, phoneOpen, setPhoneOpen, assistantOpen, setAssistantOpen])

  const createActions = useMemo(() => orderCreateActions(allowedCreateActions(can), pathname), [can, pathname])
  const canCreate = !createSuppressed && createActions.length > 0 && createMenuShownOn(pathname, can)

  const items: WheelItem[] = useMemo(() => {
    if (mode === 'create') {
      return [
        { id: 'back', label: 'Back to menu', icon: ArrowLeft, tone: 'neutral', active: false, run: () => swapMode('nav') },
        ...createActions.map((a): WheelItem => ({ id: a.kind, label: a.label, icon: a.icon, tone: a.tone, active: false, run: () => navigate(a.to) })),
      ]
    }
    const create: WheelItem[] = canCreate
      ? [{ id: 'create', label: 'Create', icon: Plus, tone: 'neutral', active: false, run: () => swapMode('create') }]
      : []
    return [...create, ...routeItems]
    // swapMode is stable (declared below with useCallback over refs/state setters only)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, createActions, canCreate, routeItems, navigate])

  const count = items.length
  const focusIdx = nearestIndex(p, count)

  // A light tick each time a new bubble takes the focus while the finger turns the wheel.
  useEffect(() => {
    if (open && live) navigator.vibrate?.(4)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusIdx])

  // ── Wheel motion ─────────────────────────────────────────────────────
  const apply = useCallback((raw: number) => {
    rawRef.current = raw
    setP(rubberBand(raw, itemsCountRef.current))
  }, [])
  const itemsCountRef = useRef(count)
  itemsCountRef.current = count

  const stopSpring = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = 0
  }, [])

  const settleTo = useCallback((target: number, velocity = 0) => {
    stopSpring()
    if (reduced) { apply(target); return }
    springRef.current = { p: rawRef.current, v: velocity }
    let last = performance.now()
    const tick = (now: number) => {
      const s = springStep(springRef.current, target, (now - last) / 1000)
      last = now
      if (atRest(s, target)) { springRef.current = { p: target, v: 0 }; apply(target); rafRef.current = 0; return }
      springRef.current = s
      apply(s.p)
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
  }, [apply, reduced, stopSpring])

  useEffect(() => stopSpring, [stopSpring])

  const jumpTo = useCallback((idx: number) => {
    setLive(true)
    settleTo(nearestIndex(idx, itemsCountRef.current))
  }, [settleTo])

  // ── Open / close ─────────────────────────────────────────────────────
  const afterFrames = (fn: () => void) => {
    if (reduced) return fn()
    requestAnimationFrame(() => requestAnimationFrame(fn))
  }

  const measureAnchor = useCallback(() => {
    const r = anchorRef.current?.getBoundingClientRect()
    if (r) setAnchor({ bottom: window.innerHeight - r.bottom, right: window.innerWidth - r.right })
  }, [])

  // Build the layer once, while the page is idle and hidden, so the first open does not pay for
  // mounting eleven bubbles in its own frame. Nothing happens at touch time: on iOS
  // Safari a tap is resolved against whatever is under the finger when it lifts, so anything
  // mounted over the button on touch-down can swallow the tap (the wheel then never opens).
  useEffect(() => {
    const t = window.setTimeout(() => { measureAnchor(); setShown(true) }, 1200)
    return () => window.clearTimeout(t)
  }, [measureAnchor])

  const toggle = () => {
    if (open) return close()
    const r = anchorRef.current?.getBoundingClientRect()
    if (r) setAnchor({ bottom: window.innerHeight - r.bottom, right: window.innerWidth - r.right })
    // Open around where you are, with Create kept in view from the everyday pages.
    const start = openPosition(items.findIndex((i) => i.active), itemsCountRef.current)
    stopSpring(); apply(start); springRef.current = { p: start, v: 0 }
    setLive(false)
    setLayerVisible(true)
    // Normally the layer was built at idle, so the bubbles spring out in this very frame. A tap
    // before that waits two frames for it to mount, so they still start from the trigger.
    if (shown) { setOpen(true); setOut(true); return }
    setShown(true)
    afterFrames(() => { setOpen(true); setOut(true) })
  }

  const close = useCallback(() => {
    stopSpring()
    setLive(false)
    setOut(false)
    setOpen(false)
  }, [stopSpring])

  const swapMode = useCallback((next: 'nav' | 'create') => {
    stopSpring()
    setLive(false)
    setOut(false)
    setMode(next)
    rawRef.current = 0
    setP(0)
    springRef.current = { p: 0, v: 0 }
    if (reduced) { setOut(true); return }
    requestAnimationFrame(() => requestAnimationFrame(() => setOut(true)))
  }, [reduced, stopSpring])

  // Once the bubbles have merged back into the trigger, hide the layer (it stays mounted, but
  // hidden elements are not hit-tested, so it can never sit over the button and eat a tap).
  useEffect(() => {
    if (open) return
    const t = window.setTimeout(() => { setLayerVisible(false); setMode('nav') }, reduced ? 0 : FOLD_MS)
    return () => window.clearTimeout(t)
  }, [open, reduced])

  // The corner moves when the phone turns or its toolbars change: keep the closed layer aligned.
  useEffect(() => {
    if (open || !shown) return
    window.addEventListener('resize', measureAnchor)
    return () => window.removeEventListener('resize', measureAnchor)
  }, [open, shown, measureAnchor])

  // A turned phone moves the corner: close rather than leave bubbles mid-air.
  useEffect(() => {
    if (!open) return
    window.addEventListener('resize', close)
    return () => window.removeEventListener('resize', close)
  }, [open, close])

  // Close when the route changes under it.
  const lastPath = useRef(pathname)
  useEffect(() => {
    if (lastPath.current !== pathname) { lastPath.current = pathname; if (open) close() }
  }, [pathname, open, close])

  // ── Keyboard ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      const at = nearestIndex(rawRef.current, itemsCountRef.current)
      if (e.key === 'Escape') { close(); triggerRef.current?.focus(); return }
      const next = e.key === 'ArrowRight' || e.key === 'ArrowUp' ? at + 1
        : e.key === 'ArrowLeft' || e.key === 'ArrowDown' ? at - 1
        : e.key === 'Home' ? 0
        : e.key === 'End' ? itemsCountRef.current - 1
        : null
      if (next === null) return
      e.preventDefault()
      jumpTo(next)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, close, jumpTo])

  // Keep keyboard focus on the bubble at the focus angle.
  useEffect(() => {
    if (!open || !out) return
    const t = window.setTimeout(() => itemRefs.current[focusIdx]?.focus({ preventScroll: true }), 60)
    return () => window.clearTimeout(t)
  }, [open, out, focusIdx, mode])

  // ── Pointer: drag around the corner to turn the wheel ────────────────
  const centre = () => anchor
    ? { x: window.innerWidth - anchor.right - TRIGGER / 2, y: window.innerHeight - anchor.bottom - TRIGGER / 2 }
    : { x: 0, y: 0 }

  const onPointerDown = (e: React.PointerEvent) => {
    if (!open || !out) return
    const c = centre()
    dragRef.current = { id: e.pointerId, x0: e.clientX, y0: e.clientY, last: pointerAngle(e.clientX, e.clientY, c.x, c.y), moved: false, v: 0, t: performance.now() }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current
    if (!d || d.id !== e.pointerId) return
    if (!d.moved) {
      if (Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < DRAG_START_PX) return
      d.moved = true
      suppressClick.current = true
      stopSpring()
      setLive(true)
      try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* not capturable: keep going */ }
    }
    const c = centre()
    // Too close to the pivot the angle is noise: ignore until the finger is out on the arc.
    if (Math.hypot(e.clientX - c.x, e.clientY - c.y) < 40) return
    const a = pointerAngle(e.clientX, e.clientY, c.x, c.y)
    const dp = scrollBy(0, angleDelta(d.last, a)) // items moved this event
    const now = performance.now()
    const dt = Math.max(1, now - d.t) / 1000
    d.v = d.v * 0.6 + (dp / dt) * 0.4
    d.last = a
    d.t = now
    apply(rawRef.current + dp)
  }

  const endDrag = (e: React.PointerEvent) => {
    const d = dragRef.current
    if (!d || d.id !== e.pointerId) return
    dragRef.current = null
    if (!d.moved) return
    settleTo(releaseTarget(rawRef.current, d.v, itemsCountRef.current), d.v)
  }

  const onWheel = (e: React.WheelEvent) => {
    if (!open || !out) return
    setLive(true)
    const target = nearestIndex(rawRef.current + Math.sign(e.deltaY || e.deltaX) * 0.7, itemsCountRef.current)
    settleTo(target)
  }

  // A drag must not also click the bubble the finger lifted over.
  const onClickCapture = (e: React.MouseEvent) => {
    if (!suppressClick.current) return
    suppressClick.current = false
    e.preventDefault()
    e.stopPropagation()
  }

  const choose = (item: WheelItem) => {
    if (item.id === 'create' || item.id === 'back') return item.run() // stays open: the ring swaps
    close()
    item.run()
  }

  // Lost is a reading surface; reserve the whole viewport for its table at idle.
  const lostView = can('opportunities.view') && pathname.replace(/\/$/, '') === '/work' && new URLSearchParams(search).get('outcome') === 'lost'
  // Phone settings status and actions must stay clear of the floating wheel.
  const phoneSettings = pathname === '/settings/phone' || pathname.startsWith('/settings/phone/')
  const teamSettings = pathname === '/settings/team' || pathname.startsWith('/settings/team/')
  if (searchOpen || callOwnsScreen || assistantOpen || lostView || phoneSettings || teamSettings) return null

  // ── The layer ────────────────────────────────────────────────────────
  const settledFar = Math.max(1, ...items.map((_, i) => Math.abs(i - p)))
  const overlay =
    shown &&
    anchor &&
    createPortal(
      <div
        className="md:hidden"
        data-testid="wheel-nav-layer"
        style={{ touchAction: 'none', visibility: layerVisible || open ? 'visible' : 'hidden' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onWheel={onWheel}
        onClickCapture={onClickCapture}
      >
        {/* Scrim: a tap outside the bubbles closes */}
        <div
          aria-hidden="true"
          onClick={close}
          className={cn(
            'fixed inset-0 z-[60] bg-black/60',
            'motion-safe:transition-opacity motion-safe:duration-200',
            open ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
        />
        <div className="fixed z-[61] pointer-events-none" style={{ bottom: anchor.bottom, right: anchor.right }}>
          <div className="relative" style={{ width: BOX, height: BOX }}>
            <div id={menuId} role="menu" aria-label={mode === 'create' ? 'Create new' : 'Navigation'} aria-hidden={!open}>
              {items.map((item, i) => {
                const angle = itemAngle(i, p)
                // Keep the full-size ring centred on the trigger: the fading tails
                // continue around the circle and off the screen (DECISIONS §24).
                const v = visibility(angle)
                const off = itemOffset(angle, out ? 1 : 0)
                const here = out && v > 0.6
                const isFocus = i === focusIdx
                const animate = !reduced && !live
                // The nearest bubbles lead out and are last back in; those off the arc stay under the trigger.
                const near = Math.min(Math.abs(i - p), 4)
                const delay = Math.round(out ? near * STAGGER_MS : (Math.min(settledFar, 4) - near) * STAGGER_MS * 0.6)
                const dur = out ? OPEN.duration : CLOSE.duration
                const Icon = item.icon
                // The bubble at the focus angle grows as it arrives (smoothstep on
                // its distance from the focus slot), so it swells under the thumb
                // while turning and springs to full size when the wheel settles.
                const t = out ? Math.max(0, 1 - Math.abs(i - p)) : 0
                const grow = out ? 1 + FOCUS_GROW * t * t * (3 - 2 * t) : 0.35
                const lit = out && isFocus && here
                return (
                  <div
                    key={`${mode}:${item.id}`}
                    data-testid={`wheel-bubble-${item.id}`}
                    // Each bubble is a solid disc in its section's colour: colour is
                    // found faster than an icon is read (.wheel-orb in index.css).
                    className={cn('wheel-orb absolute top-0 left-0 rounded-full', `tn-${item.tone}`, item.active && 'is-on', lit && 'is-focus')}
                    style={{
                      width: BUBBLE,
                      height: BUBBLE,
                      minWidth: 44,
                      minHeight: 44,
                      // Folded, a bubble is a small disc hidden under the trigger; out on the arc it is full size.
                      transform: `translate3d(${CENTRE + off.x - BUBBLE / 2}px, ${CENTRE + off.y - BUBBLE / 2}px, 0) scale(${grow})`,
                      opacity: out ? v : 0,
                      // Opacity switches instantly when animating: on as the pop starts, off once folded under the trigger.
                      transition: animate
                        ? `transform ${dur}ms ${out ? OPEN_EASE : CLOSE.easing} ${delay}ms, opacity 0ms linear ${out ? delay : delay + dur}ms`
                        : 'none',
                      willChange: 'transform',
                    }}
                  >
                    <button
                      ref={(el) => { itemRefs.current[i] = el }}
                      type="button"
                      role="menuitem"
                      tabIndex={open && here && isFocus ? 0 : -1}
                      aria-label={item.label}
                      aria-current={item.active ? 'page' : undefined}
                      data-testid={`wheel-item-${item.id}`}
                      data-focus={isFocus ? '' : undefined}
                      onClick={() => choose(item)}
                      className={cn(
                        'absolute inset-0 rounded-full flex items-center justify-center text-current',
                        'min-h-[44px] min-w-[44px]',
                        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
                        open && here ? 'pointer-events-auto' : 'pointer-events-none',
                      )}
                      style={{ background: 'transparent', WebkitTapHighlightColor: 'transparent', touchAction: 'none' }}
                    >
                      <Icon className="relative w-[23px] h-[23px]" aria-hidden="true" />
                      {item.id === '/inbox' && inboxBadge > 0 && <UnreadDot testId="wheel-item-inbox-dot" className="top-[3px] right-[3px]" />}
                    </button>
                    {/* Name of the bubble at the focus angle only, to its left.
                        A word on every bubble stacks along the arc. The button keeps
                        the accessible name; this pill is decorative and must not
                        cover a neighbour. */}
                    <span
                      aria-hidden="true"
                      className={cn(
                        'wheel-label pointer-events-none absolute right-full top-1/2 mr-3 -translate-y-[78%] origin-right whitespace-nowrap',
                        'rounded-full px-3.5 py-1.5 text-[14px] font-semibold tracking-[-0.01em]',
                        open && lit ? 'opacity-100 translate-x-0 scale-100' : 'opacity-0 translate-x-3 scale-75',
                      )}
                      style={{
                        // The label lands just after its bubble has sprung out; never under reduced motion.
                        transitionDelay: open && out && !live && !reduced ? '90ms' : '0ms',
                      }}
                    >
                      {item.label}
                    </span>
                  </div>
                )
              })}
            </div>
            {/* The trigger's stand-in, above the bubbles so they emerge from under it. Its button closes. */}
            <div
              className="absolute rounded-full"
              style={{ left: BOX - TRIGGER, top: BOX - TRIGGER, width: TRIGGER, height: TRIGGER, background: FILL, border: EDGE, boxShadow: SHADOW }}
            >
              <button
                type="button"
                onClick={close}
                aria-label="Close navigation"
                tabIndex={open ? 0 : -1}
                // Only takes touches while open. Built early (see the idle prewarm), it sits right over the
                // real bubble, and a tap that lands on it would close instead of open.
                className={cn(
                  'rounded-full flex items-center justify-center text-white',
                  open ? 'pointer-events-auto' : 'pointer-events-none',
                )}
                style={{ width: TRIGGER, height: TRIGGER, background: 'transparent', WebkitTapHighlightColor: 'transparent', touchAction: 'none' }}
              >
                <Plus
                  className={cn('w-7 h-7 motion-safe:transition-transform motion-safe:duration-200', open && 'rotate-45')}
                  style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.4, 0.64, 1)' }}
                />
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body,
    )

  return (
    // The shell spans the screen; only the bubble itself takes touches.
    <MobileDockShell
      className="md:hidden"
      insetTarget="children"
      reserveSpace
      gutterX={GUTTER_X}
      style={{ paddingBottom: DOCK_GAP, pointerEvents: 'none' }}
    >
      <div className="flex justify-end" data-testid="wheel-nav">
        {/* Hidden in place, never unmounted: the reserved space (--dock-h) never changes. */}
        <div
          ref={fabRef}
          data-step-aside={stepAside ? '' : undefined}
          inert={stepAside || undefined}
          aria-hidden={stepAside || undefined}
          className={cn(
            'motion-safe:transition-[opacity,transform] motion-safe:duration-150 motion-safe:ease-out',
            stepAside ? 'pointer-events-none opacity-0 scale-90' : 'pointer-events-auto opacity-100',
          )}
        >
          <div ref={anchorRef} className="relative shrink-0" style={{ width: TRIGGER, height: TRIGGER }}>
            <button
              ref={triggerRef}
              type="button"
              onClick={toggle}
              // While open the overlay's own close bubble takes over: hide this covered one from
              // assistive tech and the tab order so there is a single "Close navigation".
              aria-label="Open navigation"
              aria-hidden={open || undefined}
              tabIndex={open ? -1 : undefined}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-controls={menuId}
              data-testid="wheel-nav-button"
              className="relative rounded-full flex items-center justify-center text-white active:scale-95 motion-safe:transition-transform"
              style={{
                width: TRIGGER,
                height: TRIGGER,
                WebkitTapHighlightColor: 'transparent',
                background: FILL,
                border: EDGE,
                boxShadow: '0 14px 34px -10px rgba(0,0,0,0.75), 0 3px 10px rgba(0,0,0,0.45)',
              }}
            >
              <WheelMark className="h-16 w-16" />
              {/* Unread messages, visible without opening the wheel (the dock's Inbox dot). */}
              {inboxBadge > 0 && !open && <UnreadDot testId="wheel-unread-dot" className="top-[2px] right-[2px]" />}
            </button>
            {overlay}
          </div>
        </div>
      </div>
    </MobileDockShell>
  )
}
